import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, TreePine, Dog, Loader2, X, MapPin, Camera, Clock } from 'lucide-react';
import { useJsApiLoader } from '@react-google-maps/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const LIBRARIES = ['places'];

const SERVICE_OPTIONS = [
  { id: 'paseo', label: 'Paseo', icon: Dog, desc: 'Retiro, paseo, ejercicio y devolución' },
  { id: 'cuidado', label: 'Cuidado', icon: Home, desc: 'Cuidado mientras viajan sus dueños' },
  { id: 'daycare', label: 'Daycare', icon: TreePine, desc: 'Cuidado diurno mientras trabajan' },
];

const PET_SIZES = [
  { id: 'pequeno', label: 'Pequeño' },
  { id: 'mediano', label: 'Mediano' },
  { id: 'grande', label: 'Grande' },
];

export default function RegisterProvider() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [form, setForm] = useState({
  business_name: '', description: '', address: '', comuna: '', phone: '',
  latitude: null, longitude: null
});
  
  // Profile photo
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  
  // Services
  const [selectedServices, setSelectedServices] = useState({});
  
  // Availability
  const [alwaysActive, setAlwaysActive] = useState(true);
  
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const fileInputRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: LIBRARIES
  });

  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const res = await api.get('/auth/me');
        const user = res.data;
        setForm(prev => ({
          ...prev,
          business_name: user.name || '',
          phone: user.phone || '',
          comuna: user.comuna || '',
          address: user.address || ''
        }));
      } catch (err) {
        navigate('/login');
      }
    };
    loadUserData();
  }, [navigate]);

  // Initialize Places Autocomplete
  useEffect(() => {
    if (isLoaded && addressInputRef.current && !autocompleteRef.current && window.google?.maps?.places) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        componentRestrictions: { country: 'cl' },
        fields: ['geometry', 'formatted_address', 'address_components']
      });

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const address = place.formatted_address || '';
          let comuna = '';
          if (place.address_components) {
            const localityComp = place.address_components.find(c =>
              c.types.includes('locality') || c.types.includes('administrative_area_level_3')
            );
            if (localityComp) comuna = localityComp.long_name;
          }
          setForm(prev => ({ ...prev, address, latitude: lat, longitude: lng, ...(comuna ? { comuna } : {}) }));
        }
      });
    }
  }, [isLoaded]);

  // Camera functions
  const startCamera = async () => {
    setShowCameraModal(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      toast.error('No se pudo acceder a la cámara');
      setShowCameraModal(false);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
        setProfilePhoto(file);
        setProfilePhotoPreview(canvas.toDataURL('image/jpeg'));
        stopCamera();
      }, 'image/jpeg', 0.8);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCameraModal(false);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    setProfilePhoto(file);
    const reader = new FileReader();
    reader.onload = (e) => setProfilePhotoPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const toggleService = (id) => {
  setSelectedServices(prev => {
    const copy = { ...prev };
    if (copy[id]) { delete copy[id]; }
    else { copy[id] = { service_type: id, price_from: '', description: '', rules: '', pet_sizes: [] }; }
    return copy;
  });
};

  const updateService = (id, field, value) => {
    setSelectedServices(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const togglePetSize = (serviceId, size) => {
    setSelectedServices(prev => {
      const svc = prev[serviceId];
      const sizes = svc.pet_sizes.includes(size) ? svc.pet_sizes.filter(s => s !== size) : [...svc.pet_sizes, size];
      return { ...prev, [serviceId]: { ...svc, pet_sizes: sizes } };
    });
  };

  const validateForm = () => {
    if (!profilePhoto && !profilePhotoPreview) {
      toast.error('La foto de perfil es obligatoria');
      return false;
    }
    if (!form.business_name?.trim()) {
      toast.error('El nombre completo es obligatorio');
      return false;
    }
    if (!form.description?.trim()) {
      toast.error('La descripción es obligatoria');
      return false;
    }
    if (!form.address?.trim()) {
      toast.error('La dirección es obligatoria');
      return false;
    }
    if (!form.comuna?.trim()) {
      toast.error('La comuna es obligatoria');
      return false;
    }
    if (!form.phone?.trim()) {
      toast.error('El teléfono es obligatorio');
      return false;
    }
    if (Object.keys(selectedServices).length === 0) {
      toast.error('Selecciona al menos un servicio');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      // Add provider role if needed
      const userRes = await api.get('/auth/me');
      const currentUser = userRes.data;
      const roles = currentUser.roles || [currentUser.role];
      if (!roles.includes('provider')) {
        await api.post('/auth/add-role', { role: 'provider' });
      }
      
      // Create provider profile
      const services = Object.values(selectedServices).map(s => ({
        ...s, price_from: Number(s.price_from) || 0
      }));
      
      const payload = {
        ...form,
        services_offered: services,
        always_active: alwaysActive,
        available_dates: [],
        service_comunas: [],
        personal_info: {}
      };
      
      await api.post('/providers', payload);
      
      // Upload profile photo if selected
      if (profilePhoto) {
        try {
          const fd = new FormData();
          fd.append('file', profilePhoto);
          await api.post('/providers/my-profile/photo', fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (err) {
          console.error('Error uploading profile photo:', err);
        }
      }
      
      toast.success('¡Perfil de cuidador creado exitosamente!');
      navigate('/provider/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear perfil');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="register-provider">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h1 className="text-3xl font-bold text-center mb-2">Registra tu Perfil de Cuidador</h1>
          <p className="text-gray-500 text-center mb-8">Completa tus datos para comenzar a recibir solicitudes</p>

          <div className="space-y-6">
            {/* Información Personal */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">Información Personal</h2>
              <p className="text-gray-500 text-sm">Datos básicos de tu perfil de cuidador</p>
            </div>

            {/* Profile Photo with Camera Option */}
            <div className="flex justify-center mb-6">
              <div className="text-center">
                <label className="block text-sm font-medium mb-2">Foto de Perfil</label>
                <div className="relative inline-block">
                  <div className="w-28 h-28 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                    {profilePhotoPreview ? (
                      <img src={profilePhotoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-10 h-10 text-gray-400" />
                    )}
                  </div>
                  {profilePhotoPreview && (
                    <button
                      type="button"
                      onClick={() => { setProfilePhoto(null); setProfilePhotoPreview(null); }}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="flex gap-2 justify-center mt-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Subir foto
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={startCamera}
                  >
                    <Camera className="w-4 h-4 mr-1" /> Selfie
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => e.target.files[0] && handleFileSelect(e.target.files[0])}
                />
                <p className="text-xs text-gray-400 mt-2">Esta foto aparecerá en tu perfil público</p>
              </div>
            </div>

            {/* Form Fields */}
            <div>
              <label className="block text-sm font-medium mb-1">Tu Nombre Completo *</label>
              <Input placeholder="Ej: María González" value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Descripción *</label>
              <textarea 
                value={form.description} 
                onChange={e => setForm({...form, description: e.target.value})} 
                placeholder="Cuéntanos sobre ti y tu experiencia con mascotas..." 
                className="w-full border rounded-xl p-3 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-[#E6202E]" 
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Dirección *</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={addressInputRef}
                    type="text"
                    placeholder="Busca tu dirección..."
                    value={form.address}
                    onChange={e => setForm({...form, address: e.target.value, latitude: null, longitude: null})}
                    className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                {form.latitude && <p className="text-xs text-green-600 mt-1">✓ Ubicación detectada</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Comuna *</label>
                <Input placeholder="Ej: Providencia" value={form.comuna} onChange={e => setForm({...form, comuna: e.target.value})} />
              </div>
            </div>
            
            <div>
  <label className="block text-sm font-medium mb-1">Teléfono *</label>
  <Input placeholder="+56 9 1234 5678" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
</div>

            {/* Services */}
            <div className="pt-6 border-t">
              <div className="text-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Servicios que Ofreces</h2>
                <p className="text-gray-500 text-sm">Selecciona los servicios que deseas ofrecer</p>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4">
                {SERVICE_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  const isSelected = !!selectedServices[opt.id];
                  return (
                    <button key={opt.id} type="button" onClick={() => toggleService(opt.id)}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${isSelected ? 'border-[#E6202E] bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <Icon className={`w-8 h-8 mx-auto mb-2 ${isSelected ? 'text-[#E6202E]' : 'text-gray-400'}`} />
                      <p className={`font-semibold text-sm ${isSelected ? 'text-[#E6202E]' : 'text-gray-600'}`}>{opt.label}</p>
                      <p className="text-xs text-gray-400 mt-1">{opt.desc}</p>
                    </button>
                  );
                })}
              </div>

              {/* Service Details */}
              {Object.entries(selectedServices).map(([id, svc]) => {
                const opt = SERVICE_OPTIONS.find(o => o.id === id);
                return (
                  <div key={id} className="border rounded-xl p-4 space-y-3 bg-gray-50 mb-3">
                    <div className="flex justify-between items-center">
                      <h3 className="font-semibold text-[#E6202E]">{opt?.label}</h3>
                      <button type="button" onClick={() => toggleService(id)} className="text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Precio desde (CLP)</label>
                        <Input type="number" placeholder="8000" value={svc.price_from} onChange={e => updateService(id, 'price_from', e.target.value)} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Tamaño de perros</label>
                        <div className="flex gap-2 mt-1">
                          {PET_SIZES.map(ps => (
                            <button key={ps.id} type="button" onClick={() => togglePetSize(id, ps.id)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${svc.pet_sizes.includes(ps.id) ? 'bg-[#E6202E] text-white' : 'bg-white border text-gray-600'}`}
                            >{ps.label}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {Object.keys(selectedServices).length === 0 && (
                <p className="text-center text-gray-400 py-2 text-sm">Selecciona al menos un servicio para continuar</p>
              )}
            </div>

            {/* Availability */}
            <div className="pt-6 border-t">
              <div className="text-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Disponibilidad</h2>
                <p className="text-gray-500 text-sm">¿Cuándo estás disponible para cuidar mascotas?</p>
              </div>

              <label className="flex items-center gap-3 cursor-pointer p-4 rounded-xl border-2 transition-all hover:border-[#E6202E]">
                <input type="checkbox" checked={alwaysActive} onChange={e => setAlwaysActive(e.target.checked)} className="w-5 h-5 accent-[#E6202E]" />
                <div>
                  <span className="font-semibold flex items-center gap-2"><Clock className="w-4 h-4" /> Siempre activo</span>
                  <p className="text-xs text-gray-500">Apareceré en las búsquedas sin importar las fechas</p>
                </div>
              </label>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <Button 
                type="button" 
                onClick={handleSubmit} 
                disabled={submitting}
                className="w-full bg-[#E6202E] hover:bg-[#D31522] py-6 text-lg"
              >
                {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Creando...</> : 'Crear Perfil de Cuidador'}
              </Button>
              <p className="text-center text-xs text-gray-400 mt-3">
                Podrás completar más datos desde "Mi Cuenta" después de registrarte
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Camera Modal */}
      {showCameraModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4 text-center">Tomar Selfie</h3>
            <div className="relative rounded-xl overflow-hidden bg-black mb-4">
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                muted 
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={stopCamera} className="flex-1">
                Cancelar
              </Button>
              <Button type="button" onClick={capturePhoto} className="flex-1 bg-[#E6202E] hover:bg-[#D31522]">
                <Camera className="w-4 h-4 mr-2" /> Capturar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

